import React, { useState } from 'react';
import StepLayout from '../components/StepLayout';
import useEnterKey from '../hooks/useEnterKey';
import { api } from '../api';
import { getOrCreateIdempotencyKey } from '../utils/idempotencyKey';
import {
  isSnapchatAndroidWebView,
  buildChromeIntentUrl,
  buildHandoffUrl,
} from '../utils/chromeHandoff';
import { SNAP_CHROME_HANDOFF_ENABLED } from '../utils/featureFlags';

// iter-26: rebuild per Selfie Upload UI Spec.
//
//   Heading        — "Upload a selfie *" (red asterisk)
//   Intro          — "Please upload a clear selfie of yourself."
//   Red callout    — "Your application will be declined if:" + 3 bullets
//                    (face not visible, filters, blurry/dark/cropped)
//                    + italic tip "upload 2-3 photos to maximise..."
//   Drop-zone      — dashed border + "+", "Tap to select photo(s)",
//                    "JPEG / PNG / HEIC / WebP · max 50 MB each"
//   File list      — same as before; each entry shows name + status
//   Disabled Next  — until at least one file selected; ⌘+Enter hint
//   No Trustpilot  — focus is the red warning; carousel would dilute it
//
// Multi-file upload still uses pre-signed S3 PUT URLs from the BE.
// iter-32 (2026-05-29): cap raised 10 MB → 50 MB. Sellers were hitting
// "file too large" on ordinary phone selfies (large HEIC / high-res
// JPEG run 10–25 MB). 50 MB sits comfortably above any real phone photo.
// Selfie bytes go browser → S3 directly via presigned PUT, so this cap
// is a pure UX gate with zero API memory cost; kept in lockstep with the
// sizeBytes ceiling in schemas/session.py.
const MAX_MB = 50;
const MAX_BYTES = MAX_MB * 1024 * 1024;

export default function Step10SelfieUpload({ sessionId, updateData, onNext }) {
  const [files, setFiles] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  // SNAP-ANDROID-SELFIE-HANDOFF (2026-11-03): Snapchat's Android in-app
  // browser opens no file chooser, so this step cannot be completed there.
  // Offer the same session in Chrome (utils/chromeHandoff.js). Every other
  // browser renders exactly as before.
  // Dormant until SNAP_CHROME_HANDOFF_ENABLED (default off; Filip 11-03).
  const [snapAndroid] = useState(() => SNAP_CHROME_HANDOFF_ENABLED && isSnapchatAndroidWebView());
  const [linkCopied, setLinkCopied] = useState(false);
  const handoffKey = snapAndroid ? getOrCreateIdempotencyKey() : null;

  const copyHandoffLink = async () => {
    const link = buildHandoffUrl(window.location.href, handoffKey);
    try {
      await navigator.clipboard.writeText(link);
      setLinkCopied(true);
    } catch {
      window.prompt('Copy this link and open it in Chrome:', link);
    }
  };

  const addFiles = (fileList) => {
    setError('');
    const next = [...files];
    for (const f of Array.from(fileList)) {
      if (f.size > MAX_BYTES) {
        setError(`${f.name}: file too large (max ${MAX_MB} MB).`);
        continue;
      }
      next.push({ file: f, s3Key: null, status: 'pending' });
    }
    setFiles(next);
  };

  const removeAt = (i) => {
    setFiles((arr) => arr.filter((_, idx) => idx !== i));
  };

  const uploadOne = async (entry, i) => {
    const { uploadUrl, s3Key } = await api.requestSelfieUploadUrl(sessionId, {
      contentType: entry.file.type,
      sizeBytes: entry.file.size,
    });
    await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': entry.file.type },
      body: entry.file,
    }).then((r) => {
      if (!r.ok) throw new Error(`S3 PUT failed (${r.status})`);
    });
    setFiles((arr) => arr.map((f, idx) => idx === i ? { ...f, s3Key, status: 'done' } : f));
    return s3Key;
  };

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (files.length === 0) {
      setError('Please add at least one selfie.');
      return;
    }
    setLoading(true);
    setSubmitError('');
    try {
      const keys = [];
      for (let i = 0; i < files.length; i++) {
        if (files[i].s3Key) {
          keys.push(files[i].s3Key);
          continue;
        }
        setFiles((arr) => arr.map((f, idx) => idx === i ? { ...f, status: 'uploading' } : f));
        const k = await uploadOne(files[i], i);
        keys.push(k);
      }
      await api.updateSelfie(sessionId, { selfieS3Keys: keys });
      updateData({ selfieS3Keys: keys });
      onNext();
    } catch (err) {
      setSubmitError(err.message || 'Upload failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = files.length > 0 && !loading;

  // Spec: ⌘+Enter (Ctrl+Enter on Windows) submits if can-submit.
  useEnterKey(() => handleSubmit({ preventDefault: () => {} }), { enabled: canSubmit, requireMeta: true });

  return (
    // iter-26: spec says NO Trustpilot on this page (red warning is the
    // focal point; reviews would dilute it).
    <StepLayout title="Upload a selfie *" hideTrustpilot>
      <p style={{
        color: '#444',
        fontSize: 16,
        lineHeight: 1.5,
        margin: 0,
        fontFamily: 'var(--font-sans)',
      }}>
        Please upload a clear selfie of yourself.
      </p>

      {snapAndroid && (
        <div className="selfie-handoff" role="note" data-testid="snap-chrome-handoff">
          <strong>Snapchat can&rsquo;t upload photos.</strong>
          <p>
            Open this page in Chrome to add your selfie. Your progress is saved
            and you&rsquo;ll continue right here.
          </p>
          <a
            className="primary-cta selfie-handoff__btn"
            href={buildChromeIntentUrl(window.location.href, handoffKey)}
          >
            Open in Chrome
          </a>
          <button type="button" className="selfie-handoff__copy" onClick={copyHandoffLink}>
            {linkCopied ? 'Link copied. Paste it into Chrome.' : 'Copy link instead'}
          </button>
        </div>
      )}

      {/* Red declined-if callout */}
      <div className="selfie-warning" role="note">
        <div className="selfie-warning__head">
          <span className="selfie-warning__icon" aria-hidden="true">&#9888;&#65039;</span>
          <strong>Your application will be declined if:</strong>
        </div>
        <ul className="selfie-warning__list">
          <li>Your full face isn&rsquo;t clearly visible</li>
          <li>The photo has any filters applied (Snapchat, Instagram, beauty filters, etc.)</li>
          <li>The image is blurry, dark, or cropped</li>
        </ul>
        <p className="selfie-warning__tip">
          <em>Tip: upload 2&ndash;3 photos to maximise your chance of approval.</em>
        </p>
      </div>

      {/* Drop-zone */}
      <input
        type="file"
        accept="image/*"
        multiple
        onChange={(e) => addFiles(e.target.files || [])}
        style={{ display: 'none' }}
        id="selfie-upload"
      />
      <label htmlFor="selfie-upload" className="selfie-dropzone">
        <span className="selfie-dropzone__plus" aria-hidden="true">+</span>
        <span className="selfie-dropzone__primary">
          {files.length === 0 ? 'Tap to select photo(s)' : 'Upload more files'}
        </span>
        <span className="selfie-dropzone__hint">
          JPEG / PNG / HEIC / WebP &middot; max {MAX_MB} MB each
        </span>
      </label>

      {files.length > 0 && (
        <div className="selfie-files">
          {files.map((entry, i) => (
            <div key={i} className="selfie-file">
              <span className="selfie-file__name">{entry.file.name}</span>
              <span className="selfie-file__status">
                {entry.status === 'uploading' ? 'uploading…' : entry.status === 'done' ? '✓' : ''}
              </span>
              <button
                type="button"
                onClick={() => removeAt(i)}
                disabled={loading}
                className="selfie-file__remove"
                aria-label={`Remove ${entry.file.name}`}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {error && <p className="form-field__error">{error}</p>}
      {submitError && <p className="form-field__error">{submitError}</p>}

      <div className="cta-row">
        <button
          type="button"
          onClick={() => handleSubmit()}
          disabled={!canSubmit}
          aria-disabled={!canSubmit}
          className="cta-row__btn primary-cta"
        >
          {loading ? 'Uploading...' : 'Next'}
        </button>
        <span className="enter-hint">press <strong>⌘ + Enter ↵</strong></span>
      </div>
    </StepLayout>
  );
}
